// pages/module-pages/job-offers/UpdateJobOfferPage.tsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BaseJobOffer, JobOffer } from "../../../types/features/JobOffer";
import useJobOffers from "../../../hooks/features/job-offers/useJobOffers";
import PageLayout from "../../../layouts/PageLayout";
import PageContainer from "../../../components/ui/PageContainer";
import JobOfferForm from "../../../components/shared/job-offers/JobOfferForm";
import useCustomParams from "../../../hooks/core/useCustomParams";
import { uploadService } from "../../../services/uploadService";
import useToken from "../../../hooks/features/auth/useToken";
import { MAIN_URL } from "../../../config/api";
import useAuth from "../../../hooks/features/auth/useAuth";
import { alertError } from "../../../helpers/alertError";

export default function UpdateJobOfferPage() {
  const { id } = useCustomParams();
  const navigate = useNavigate();
  const { fetchJobOfferById, updateJobOffer } = useJobOffers();
  const { user } = useAuth();
  const { token } = useToken();
  const [jobOffer, setJobOffer] = useState<JobOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadJobOffer();
  }, [id]);

  const loadJobOffer = async () => {
    if (id) {
      const data = await fetchJobOfferById(id);
      setJobOffer(data);
      setLoading(false);
    }
  };

  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const handleSubmit = async (data: BaseJobOffer, imageFile?: File) => {
    setIsLoading(true);

    if (!user?.id || user.role !== "ADMIN") {
      alertError("Veuillez vous connecter en tant qu'admin avant de modifier");
      setIsLoading(false);
      return;
    }

    let coverImageUrl = data.cover_image;

    // Upload de la nouvelle image si présente
    if (imageFile) {
      const uploadResult = await uploadService.uploadFile({
        type: "image",
        file: imageFile,
        reason: "job_offer",
        user_id: user?.id,
      });
      coverImageUrl = `${uploadResult.url}`;
    }

    const slug = generateSlug(data.title);
    const success = await updateJobOffer(id!, {
      ...data,
      slug,
      cover_image: coverImageUrl,
    });

    setIsLoading(false);
    if (success) {
      navigate("/admin/job-offers");
    }
  };

  if (loading) {
    return (
      <PageLayout title="Chargement..." className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Modifier l'offre d'emploi" className="p-6 max-w-4xl mx-auto">
      <PageContainer>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Modifier l'offre d'emploi</h1>
          <p className="text-gray-500 mt-1">Modifiez les informations de l'offre</p>
        </div>
        <JobOfferForm initialData={jobOffer as JobOffer} isEdit={true} onSubmit={handleSubmit} isLoading={isLoading} />
      </PageContainer>
    </PageLayout>
  );
}
